import { Component, DestroyRef, effect, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { SidepanelService } from '../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../shared/components/toastr/toastr-success.component';
import { FamilleLivrables } from '../models/famille-livrables.model';
import { FamilleLivrablesUpdateRequest } from '../requests/famille-livrables-update-request';
import { FamilleLivrablesStore } from '../store/famille-livrables.store';
import { Référentiel } from '../../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-form-famille-livrables',
  imports: [ReactiveFormsModule, HlmFieldImports, HlmInputImports, LabelRequiredDirective],
  templateUrl: './form-famille-livrables.component.html',
})
export class FormFamilleLivrablesComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #familleLivrablesStore = inject(FamilleLivrablesStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #destroyRef = inject(DestroyRef);

  readonly famille = input.required<FamilleLivrables>();
  readonly referentiel = input.required<Référentiel>();

  readonly form = this.#fb.nonNullable.group({
    nom: [null, Validators.required],
  });

  constructor() {
    effect(() => {
      const famille = this.famille();

      this.nom.setValue(famille.nom);
    });
  }

  ngOnInit(): void {
    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  onSubmit() {
    if (this.nom.hasError('required')) {
      return;
    }
    const toastrId = uuidv7();

    const famille: FamilleLivrablesUpdateRequest = {
      nom: this.nom.value.trim(),
    };

    if (!famille.nom.length) return;

    this.#familleLivrablesStore.modifier({
      idFamilleLivrables: this.famille().id,
      request: famille,
      next: () => {
        this.#familleLivrablesStore.charger({
          idCartographie: this.referentiel().idCartographie,
          idRéférentiel: this.referentiel().id,
        });
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message: 'Édition de la famille réussie',
            id: toastrId,
          },
          id: toastrId,
        });
        this.#sidepanelService.close();
      },
      error: () => this.alreadyExists(famille),
    });
  }

  alreadyExists(famille: { nom: string }) {
    const familles = this.#familleLivrablesStore.entities().map((x) => x.nom);
    const doublonsAvecFamillesExistantes = familles.filter((x: string) => famille.nom === x);

    if (doublonsAvecFamillesExistantes.length > 0) {
      this.nom.setErrors({ notUnique: true });
    }
  }

  get nom() {
    return this.form.get('nom') as AbstractControl;
  }
}
